import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  AtSign,
  BadgeDollarSign,
  Check,
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  DatabaseBackup,
  FileSearch,
  FileSignature,
  Hourglass,
  Link2,
  Mail,
  MailQuestion,
  MousePointerClick,
  PauseCircle,
  PhoneIncoming,
  Siren,
  UserCheck,
  UserCog,
  UserPlus,
  Wallet,
} from 'lucide-react';
import { cn } from '@/lib/utils.js';
import { toast } from '@/components/ui/toaster.jsx';
import { getQueue, clearQueueNotification, doorForItem, attachPaperPhf } from '@/services/queue.service.js';
import { toDate } from '@/lib/date-display.js';
import { clearLabel, clearTitle, sourceLabel, cutLine, splitSourceErrors, SOURCE_ERRORS_HEAD, SOURCE_ERRORS_DETAILS } from '@/lib/queue-labels.js';
import UsePilotValueButton from '@/components/queue/UsePilotValueButton.jsx';
import PossibleMatchButtons from '@/components/queue/PossibleMatchButtons.jsx';
import PeopleToConfirm, { PEOPLE_TO_CONFIRM_KINDS, callConfirmLinks } from '@/components/queue/PeopleToConfirm.jsx';

// The what's-waiting queue (MV-04 2.1 + MV-10 3.1, Ship 4a): the LANDING
// SURFACE. 8am shows what needs the broker right now, urgency-sorted. Every
// item is one line (what, which deal, how old) + ONE primary action landing
// on the exact door. Items DERIVE from underlying state; resolving the cause
// clears them without refresh games (we refetch after every action).
// Endpoint: /api/tracon/queue. Broker-scoped by default, all-agency toggle.

const KIND_ICON = {
  payment_cleared: BadgeDollarSign,
  payment_short: AlertTriangle,
  payment_unreconciled: Siren,
  ledger_write_failed: Siren,
  bind_effective_unreconciled: Siren,
  bind_effective_precedes_payment: Siren,
  bind_signature_unrecorded: Siren,
  crm_mirror_broken: Siren,
  fronted_premium: Siren,
  unremitted_aging: Hourglass,
  commission_rate_missing: AlertTriangle,
  intake_unresolved: FileSearch,
  statement_needed: FileSearch,
  intake_silent: MailQuestion,
  account_merge_suggested: AlertTriangle,
  deferred_collection: Wallet,
  quote_expiring: Hourglass,
  escalation: Siren,
  nudge_due: MailQuestion,
  quote_draft: FileSearch,
  quote_triage: MousePointerClick,
  quote_mail: Mail,
  paper_draft: ClipboardCheck,
  paper_phf_unattached: ClipboardList,
  paper_phf_unlanded: AlertTriangle,
  pilot_unlinked: UserPlus,
  signature_completed: FileSignature,
  phf_submitted: UserCheck,
  pilot_email_changed: Mail,
  typed_email_matches_client: UserPlus,
  pilot_form_differs: UserCog,
  name_still_needed: UserCog,
  phone_intake_call: PhoneIncoming,
  possible_match: UserCog,
  client_mail_no_address: MailQuestion,
  needs_own_email: AtSign,
  account_link_notice: Link2,
  backup_failed: DatabaseBackup,
  backup_stale: DatabaseBackup,
  backup_restore_test: DatabaseBackup,
  backup_drill_due: DatabaseBackup,
  parked_intake: PauseCircle,
  awaiting_client: Hourglass,
  market_waiting: Hourglass,
  bind_held: PauseCircle,
  bind_holds_unreadable: AlertTriangle,
  bind_request_flags: FileSignature, // ruling G: what changed from submission, information only
  signed_app_link_review: FileSearch, // R2d-Q2: the differing signed-application links
  imported_documents_to_name: FileSearch, // paperwork-1 L4-2: the WorkDrive imports to name
};

// Stored pure notifications carry a Clear action; everything else clears
// itself when its cause resolves.
// account_merge_suggested: David merges (or not) in Zoho; nothing in TRACON
// resolves it, so it clears by hand (section 16 item 4).
// pilot_email_changed: an FYI (ruling (a)2); its title starts "FYI:" and the
// broker clears it once read. typed_email_matches_client the same (ruling
// (a)3): nothing in TRACON resolves it; David merges in Zoho or leaves it.
// pilot_form_differs (R4): the sync held a pilot's name or phone for David;
// clearing it records Keep mine (lib/queue-labels.js says so on the button,
// through its own door); Use the pilot's sits beside it (T8e).
// name_still_needed (ruling 4, people-create-door F1): a filed bind document
// printed a blank; David sets the name in TRACON and clears it by hand.
// phone_intake_call (David 2026-10-19): a caller to follow up; nothing in
// TRACON resolves it, so David clears it once he has called back.
// possible_match (David 2026-10-20): two person records that may be one person;
// David answers it, Same person or Not the same, through its own door (PLAN-ROUND3 1.3).
// client_mail_no_address (Q26, PLAN-ROUND4 2.1): client mail that had no address; David
// sends it by hand and clears it.
// needs_own_email (David's ruling of 2026-10-22, PLAN-ROUND6 4.1): a person with no email
// of his own. It clears itself when an address of his own is written; Clear is David's
// hand (e.g. after he fixes the Email in Zoho). A notice only: nothing sends from it.
// account_link_notice (account-people-door T02, PLAN-FINAL 2.2): a person's link to an account
// that needs David's eye (not saved, primary not changed, main no longer stated, an ended link
// left ended, a moved pilot form). Nothing in TRACON resolves it; David clears it by hand.
// backup_failed, backup_stale, backup_restore_test, backup_drill_due (David's brief of 2026-10-26):
// the off-site backup alerts; RESTORE.md says what to do. David clears each by hand once read.
const NOTIFICATION_KINDS = new Set(['payment_cleared', 'signature_completed', 'phf_submitted', 'account_merge_suggested', 'pilot_email_changed', 'typed_email_matches_client', 'pilot_form_differs', 'name_still_needed', 'phone_intake_call', 'possible_match', 'client_mail_no_address', 'needs_own_email', 'account_link_notice', 'backup_failed', 'backup_stale', 'backup_restore_test', 'backup_drill_due']);

// A kind whose title names a record, not the work: the line says the work first.
// account_link_notice: six titles share one kind, so the line names the kind first.
const KIND_LABEL = { needs_own_email: 'Needs own email', account_link_notice: 'Account link', backup_failed: 'Backup', backup_stale: 'Backup', backup_restore_test: 'Restore test', backup_drill_due: 'Restore drill' };

const SECTION_META = [
  { key: 'now', heading: 'Now', hint: 'Money, expiring offers, blocked decisions' },
  { key: 'today', heading: 'Today', hint: 'Reviews waiting, follow-ups due, arrivals to place' },
  { key: 'waiting', heading: 'Waiting on others', hint: 'Sent and quiet. Read-mostly; nothing to do yet' },
];

export function formatAge(iso, nowMs = Date.now()) {
  if (!iso) return '';
  const ms = nowMs - (toDate(iso)?.getTime() ?? NaN);
  if (!Number.isFinite(ms)) return '';
  if (ms < 0) {
    const days = Math.ceil(-ms / 86400000);
    return days === 0 ? 'today' : `in ${days}d`;
  }
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${Math.max(mins, 1)}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

// A paper PHF that arrived with no deal. Its action is inline rather than a
// door, because there is no deal to open yet: picking one IS the work. Mirrors
// the quote-inbox "needs a match" control, which solves the same shape of
// problem for an arrived document.
function AttachPaperPhf({ item, onAttached }) {
  const [dealId, setDealId] = useState('');
  const [busy, setBusy] = useState(false);

  async function attach() {
    const id = dealId.trim();
    if (!id) return;
    setBusy(true);
    try {
      await attachPaperPhf(item.refs.phf_id, id);
      toast.success('Attached. Confirm them in that deal’s Pilots panel to add to the roster.');
      onAttached();
    } catch (err) {
      toast.error(err.body?.error || err.message || 'Attach failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex shrink-0 items-center gap-1.5">
      <input
        placeholder="Deal id"
        value={dealId}
        onChange={(e) => setDealId(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') attach(); }}
        className="w-40 rounded border border-border bg-bg px-2 py-1.5 text-[12.5px] text-text"
      />
      <button
        type="button"
        disabled={busy || !dealId.trim()}
        onClick={attach}
        className="inline-flex min-h-[36px] items-center gap-1.5 rounded-[5px] border border-accent bg-accent px-3 py-1.5 text-[12.5px] text-white hover:brightness-110 disabled:opacity-50"
      >
        {busy ? 'Attaching…' : 'Attach to deal'}
      </button>
    </div>
  );
}

function QueueRow({ item, onClear, clearing, onAttached, onDecided, confirmLinks = [] }) {
  // Keep mine and Use the pilot's decide one flag: neither runs while the other is in flight.
  const [adopting, setAdopting] = useState(false);
  const Icon = KIND_ICON[item.kind] || Hourglass;
  const door = doorForItem(item);
  const isNotification = NOTIFICATION_KINDS.has(item.kind);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-card border border-border bg-surface px-3.5 py-2.5">
      <Icon size={16} strokeWidth={1.5} className={cn('shrink-0', item.section === 'now' ? 'text-accent' : 'text-text-dim')} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-text">{item.title}</p>
        <p className="text-[11px] text-text-muted">
          {KIND_LABEL[item.kind] ? <span>{KIND_LABEL[item.kind]} {'·'} </span> : null}
          {item.deal_id ? (
            <Link
              to={`/account/${encodeURIComponent(item.deal_id)}/deal/${encodeURIComponent(item.deal_id)}`}
              className="hover:underline"
            >
              {item.deal_name || `Deal ${item.deal_id}`}
            </Link>
          ) : (
            <span>No deal yet</span>
          )}
          {item.detail ? <span> {'·'} {item.detail}</span> : null}
          {item.age_at ? <span> {'·'} {formatAge(item.age_at)}</span> : null}
        </p>
      </div>
      {door ? (
        <Link
          to={door.href}
          className="inline-flex min-h-[36px] shrink-0 items-center gap-1.5 rounded-[5px] border border-accent bg-accent px-3 py-1.5 text-[12.5px] text-white hover:brightness-110"
        >
          {door.label}
        </Link>
      ) : null}
      {item.kind === 'paper_phf_unattached' && item.refs?.phf_id ? (
        <AttachPaperPhf item={item} onAttached={onAttached} />
      ) : null}
      {isNotification && item.refs?.notification_id && item.kind !== 'possible_match' ? (
        <button
          type="button"
          title={clearTitle(item.kind)}
          disabled={clearing || adopting}
          onClick={() => onClear(item)}
          className="inline-flex min-h-[36px] shrink-0 items-center gap-1 rounded-[5px] border border-border bg-surface px-2.5 py-1.5 text-[12.5px] text-text-muted hover:border-border-strong hover:text-text disabled:opacity-50"
        >
          <Check size={14} strokeWidth={1.5} /> {clearLabel(item.kind)}
        </button>
      ) : null}
      {item.kind === 'pilot_form_differs' ? <UsePilotValueButton item={item} onDone={onDecided} disabled={clearing} onBusyChange={setAdopting} /> : null}
      {item.kind === 'possible_match' ? <PossibleMatchButtons item={item} onDone={onDecided} /> : null}
      {confirmLinks.map((l) => (
        <a key={l.href} href={l.href} className="inline-flex min-h-[36px] shrink-0 items-center rounded-[5px] border border-border bg-surface px-2.5 py-1.5 text-[12.5px] text-text-muted hover:border-border-strong hover:text-text">
          {l.label}
        </a>
      ))}
    </div>
  );
}

export default function Queue() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [allAgency, setAllAgency] = useState(false);
  const [clearingId, setClearingId] = useState(null);

  const refresh = useCallback((all = allAgency) => {
    getQueue({ allBrokers: all })
      .then((d) => { setData(d); setError(''); })
      .catch((err) => setError(err.body?.error || err.message || 'Load failed'));
  }, [allAgency]);

  useEffect(() => { refresh(); }, [refresh]);

  // Come back to the tab → the queue re-derives (no refresh games).
  useEffect(() => {
    const onVisible = () => { if (!document.hidden) refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh]);

  async function clearNotification(item) {
    setClearingId(item.refs.notification_id);
    // Optimistic: the row leaves immediately; refetch confirms.
    setData((d) => d ? { ...d, items: d.items.filter((i) => i.key !== item.key) } : d);
    try {
      await clearQueueNotification(item);
    } catch (err) {
      toast.error(err.body?.error || err.message || 'Clear failed');
    } finally {
      setClearingId(null);
      refresh();
    }
  }

  const items = data?.items || [];
  const done = data?.done_today || [];
  // A source that failed or was cut (Q13): said under the header, never cleared, retried or hidden here.
  const source_errors = Array.isArray(data?.source_errors) ? data.source_errors : [];
  const sourceUnread = splitSourceErrors(source_errors).unread;
  const bySection = { now: [], today: [], waiting: [] };
  for (const i of items) (bySection[i.section] || bySection.today).push(i);

  return (
    <div className="mx-auto w-full max-w-[860px] p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-semibold text-text">What&rsquo;s waiting</h2>
          <p className="mt-0.5 text-sm text-text-muted">
            Everything that needs you, loudest first. Items clear themselves when their cause resolves.
          </p>
        </div>
        {/* All-agency toggle (visibility unconditional; scope is a filter, not a gate). */}
        <label className="flex shrink-0 cursor-pointer items-center gap-2 text-[12px] text-text-muted">
          <input
            type="checkbox"
            checked={allAgency}
            onChange={(e) => { setAllAgency(e.target.checked); }}
            className="h-4 w-4 accent-[#C62D48]"
          />
          All agency
        </label>
      </div>
      {data?.scope === 'unresolved' && !allAgency ? (
        <p className="mt-2 rounded-card border border-border bg-surface px-3 py-1.5 text-[11px] text-text-dim">
          Showing all agency items: your login could not be matched to a producer record.
        </p>
      ) : null}
      {source_errors.length > 0 && (
        <div className="mt-2 rounded-card border border-border bg-surface px-3 py-2 text-[12px] text-text-muted">
          <p className="font-medium text-warning">{SOURCE_ERRORS_HEAD}</p>
          {sourceUnread.length > 0 ? <p className="mt-0.5 text-text">{sourceUnread.join(', ')}</p> : null}
          {source_errors.filter((e) => e?.cut).map((e, i) => (
            <p key={`cut-${i}`} className="mt-0.5 text-text">{cutLine(e)}</p>
          ))}
          <details className="mt-1">
            <summary className="cursor-pointer text-[11px] text-text-dim">{SOURCE_ERRORS_DETAILS}</summary>
            <ul className="mt-1 space-y-0.5 text-[11px] text-text-dim">
              {source_errors.map((e, i) => (
                <li key={`err-${i}`} className="break-words">{sourceLabel(e?.source)}: {e?.error || 'no error text'}</li>
              ))}
            </ul>
          </details>
        </div>
      )}

      {error ? (
        <p className="mt-6 text-sm text-warning">{error}</p>
      ) : data === null ? (
        <p className="mt-6 text-sm text-text-muted">Deriving the queue&hellip;</p>
      ) : items.length === 0 ? (
        <div className="mt-6 rounded-card border border-dashed border-border p-6 text-sm text-text-muted">
          Nothing needs you right now. Quote mail, parse drafts, paperwork reviews, due follow-ups,
          expiring offers, and client completions (payments, signatures, PHFs) land here as they happen.
        </div>
      ) : (
        <div className="mt-5 space-y-6">
          {SECTION_META.map(({ key, heading, hint }) => {
            const rows = bySection[key];
            if (!rows.length) return null;
            // People to confirm (R26, Q14): its kinds sit in one block at the top of Today.
            const peopleRows = key === 'today' ? rows.filter((i) => PEOPLE_TO_CONFIRM_KINDS.has(i.kind)) : [];
            const plainRows = key === 'today' ? rows.filter((i) => !PEOPLE_TO_CONFIRM_KINDS.has(i.kind)) : rows;
            return (
              <section key={key}>
                <div className="mb-2 flex items-baseline gap-2">
                  <h3 className={cn(
                    'text-[11px] font-semibold uppercase tracking-wider',
                    key === 'now' ? 'text-accent' : 'text-text-dim'
                  )}>
                    {heading} {'·'} {rows.length}
                  </h3>
                  <span className="hidden text-[11px] text-text-dim sm:inline">{hint}</span>
                </div>
                <div className="space-y-2">
                  {key === 'today' && peopleRows.length ? <PeopleToConfirm items={peopleRows} onChanged={() => refresh()} onClear={clearNotification} clearingId={clearingId} ageOf={formatAge} /> : null}
                  {plainRows.map((i) => (
                    <QueueRow key={i.key} item={i} onClear={clearNotification} clearing={clearingId === i.refs?.notification_id} onAttached={refresh} onDecided={() => refresh()} confirmLinks={callConfirmLinks(i, items)} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {data !== null && done.length > 0 ? (
        <details className="mt-8 rounded-card border border-border bg-surface">
          <summary className="cursor-pointer list-none px-3.5 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-text-dim">
            Done today {'·'} {done.length}
          </summary>
          <div className="space-y-1.5 px-3.5 pb-3">
            {done.map((d, i) => (
              <p key={i} className="flex items-center gap-2 text-[12.5px] text-text-muted">
                <CheckCircle2 size={14} strokeWidth={1.5} className="shrink-0 text-success" />
                <span className="min-w-0 flex-1 truncate">
                  {d.title}
                  {d.deal_name ? ` · ${d.deal_name}` : ''}
                </span>
                <span className="shrink-0 text-[11px] text-text-dim">{formatAge(d.at)}</span>
              </p>
            ))}
          </div>
        </details>
      ) : null}
    </div>
  );
}
